import {
  Text,
  Center,
  Heading,
  Button,
  useToast,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Badge,
  Box,
  VStack,
  HStack,
  Divider,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import { basicBenefits, pricingList } from "../pricingList";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { FormattedMessage, useIntl } from "react-intl";
import { FaCheckCircle, FaCrown, FaRocket, FaStar, FaGem, FaBuilding, FaIndustry } from "react-icons/fa";
import "./PricingTable.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useNavigate } from "react-router-dom";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import useApiRequest from "hooks/api/useApiHook";
import { useEffect, useState, useRef } from "react";
import { setSubscription } from "redux/slices/userDataSlice";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";

export const BuySubscriptionButton = ({
  balance,
  item,
  currentSubscription,
}) => {
  const [subscriptionId, setSubscriptionId] = useState(null);
  const { isUserLoggedIn } = useSelector((state: RootState) => state.userData);
  const apiKey = useSelector((state: RootState) => state.apiKey.apiKey);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const finalRef = useRef(null);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const toast = useToast();
  const intl = useIntl();

  const { data } = useApiRequest({
    apiToken: apiKey,
    method: RequestMethod.GET,
    name: RequestName.BUY_SUBSCRIPTION,
    buySubscriptionParams: {
      subscriptionId: subscriptionId,
    },
  });

  const defaultApiKey = process.env.REACT_APP_DEFAULT_API_KEY;

  const isAbleToPerform = apiKey !== defaultApiKey && balance >= 0;

  const handleBuyBtnClick = (subscription) => {
    if (!isUserLoggedIn) {
      navigate("/auth/log-in");
    } else if (isAbleToPerform && balance < subscription.price) {
      toast({
        title: intl.formatMessage({ id: "InsufficientFundsTitle" }),
        description: intl.formatMessage({ id: "InsufficientFundsDescription" }),
        status: "warning",
        duration: 2500,
        isClosable: true,
        position: "top",
      });
      navigate("/top-up-balance");
    } else if (isAbleToPerform && balance >= subscription.price) {
      if (!currentSubscription) {
        setSubscriptionId(subscription.id);
      } else if (currentSubscription < subscription.id) {
        onOpen();
      }
    }
  };

  useEffect(() => {
    if (subscriptionId) {
      if (data?.status === "success") {
        toast({
          title: intl.formatMessage({ id: "PurchaseSuccessfulTitle" }),
          description: intl.formatMessage({ id: "PurchaseSuccessfulDescription" }),
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        dispatch(setSubscription(subscriptionId));

        navigate("/profile/subscriptions");
      } else {
        toast({
          title: intl.formatMessage({ id: "SomethingWentWrongTitle" }),
          description: intl.formatMessage({ id: "SomethingWentWrongDescription" }),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    }
  }, [data, navigate, toast, intl, subscriptionId, dispatch]);

  return (
    <>
      <Button
        onClick={() => handleBuyBtnClick(item)}
        height="35px"
        colorScheme="blue"
      >
        <Text mr={1} fontSize={16} fontWeight="bold">
          ${item.price}
        </Text>
        /<FormattedMessage id="MonthText" />
      </Button>
      <>
        <Modal finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <FormattedMessage id="SubscriptionUpgradeTitle" />
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormattedMessage id="SubscriptionUpgradeMessage" />
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={onClose}>
                <FormattedMessage id="CloseButton" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => setSubscriptionId(item.id)}
              >
                <FormattedMessage id="UpgradeButton" />
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    </>
  );
};

const PricingTable = () => {
  const [balance, setBalance] = useState(null);
  const apiKey = useSelector((state: RootState) => state.apiKey.apiKey);
  const currentSubscription = useSelector(
    (state: RootState) => state.userData.subscription
  );
  const { isUserLoggedIn } = useSelector((state: RootState) => state.userData);
  const navigate = useNavigate();
  const toast = useToast();

  const { data, loading, error } = useApiRequest({
    apiToken: apiKey,
    method: RequestMethod.GET,
    name: RequestName.GET_BALANCE,
  });

  useEffect(() => {
    if (data && data.status === "success") {
      setBalance(data.data.balance);
    }
  }, [data, error, loading]);

  // Component for purchasing subscription in PricingTable
  const BuySubscriptionButtonTable = ({ subscription }) => {
    const [subscriptionId, setSubscriptionId] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const finalRef = useRef(null);
    const dispatch = useDispatch();
    const intl = useIntl();

    const { data } = useApiRequest({
      apiToken: apiKey,
      method: RequestMethod.GET,
      name: RequestName.BUY_SUBSCRIPTION,
      buySubscriptionParams: {
        subscriptionId: subscriptionId,
      },
    });

    const defaultApiKey = process.env.REACT_APP_DEFAULT_API_KEY;
    const isAbleToPerform = apiKey !== defaultApiKey && balance >= 0;

    const handleBuyBtnClick = (subscription) => {
      if (!isUserLoggedIn) {
        navigate("/auth/log-in");
      } else if (isAbleToPerform && balance < subscription.price) {
        toast({
          title: intl.formatMessage({ id: "InsufficientFundsTitle" }),
          description: intl.formatMessage({ id: "InsufficientFundsDescription" }),
          status: "warning",
          duration: 2500,
          isClosable: true,
          position: "top",
        });
        navigate("/top-up-balance");
      } else if (isAbleToPerform && balance >= subscription.price) {
        if (!currentSubscription) {
          setSubscriptionId(subscription.id);
        } else if (currentSubscription < subscription.id) {
          onOpen();
        }
      }
    };

    useEffect(() => {
      if (subscriptionId) {
        if (data?.status === "success") {
          toast({
            title: intl.formatMessage({ id: "PurchaseSuccessfulTitle" }),
            description: intl.formatMessage({ id: "PurchaseSuccessfulDescription" }),
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          dispatch(setSubscription(subscriptionId));
          navigate("/profile/subscriptions");
        } else if (data?.status === "error") {
          toast({
            title: intl.formatMessage({ id: "PurchaseErrorTitle" }),
            description: data.message || intl.formatMessage({ id: "PurchaseErrorDescription" }),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      }
    }, [data, subscriptionId, dispatch, intl]);

    return (
      <>
        <Button
          onClick={() => handleBuyBtnClick(subscription)}
          colorScheme={subscription.id === 3 ? "green" : "blue"}
          size="lg"
          width="100%"
          borderRadius="full"
          py={6}
          fontSize="lg"
          fontWeight="bold"
          _hover={{
            transform: "translateY(-2px)",
            boxShadow: "lg"
          }}
          transition="all 0.2s"
        >
          <FormattedMessage id="GetStartedText" defaultMessage="Get Started" />
        </Button>
        
        <Modal finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <FormattedMessage id="SubscriptionUpgradeTitle" />
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormattedMessage id="SubscriptionUpgradeMessage" />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={onClose}>
                <FormattedMessage id="CloseButton" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => setSubscriptionId(subscription.id)}
              >
                <FormattedMessage id="UpgradeButton" />
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  };

  // Icons for plans
  const getPlanIcon = (planId: number) => {
    switch (planId) {
      case 1: return FaRocket;
      case 2: return FaStar;
      case 3: return FaCrown;
      case 4: return FaGem;
      case 5: return FaBuilding;
      case 12: return FaIndustry;
      case 13: return FaIndustry;
      default: return FaRocket;
    }
  };

  // Plan card component
  const PricingCard = ({ plan, isPopular = false }) => {
    const isSubscriptionAvailable =
      (!currentSubscription && plan.id > 1) ||
      (currentSubscription && plan.id > currentSubscription);
    const isFreePlan = plan.id === 1;
    const isCurrentSubscription =
      currentSubscription && currentSubscription === plan.id;

    const PlanIcon = getPlanIcon(plan.id);

    return (
      <Box position="relative">
        <BoxShadowContainer>
          <Box
            p={6}
            borderRadius="xl"
            border={isPopular ? "2px solid" : "1px solid"}
            borderColor={isPopular ? "status.success" : "border.subtle"}
            bg="bg.surface"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "xl",
              transition: "all 0.3s ease"
            }}
            transition="all 0.3s ease"
          >
        {isPopular && (
          <Badge
            position="absolute"
            top="-10px"
            left="50%"
            transform="translateX(-50%)"
            colorScheme="green"
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="sm"
            fontWeight="bold"
          >
            <FormattedMessage id="PopularText" />
          </Badge>
        )}

        <VStack spacing={4} align="stretch">
          {/* Plan header */}
          <VStack spacing={2}>
            <HStack spacing={3}>
              <Icon as={PlanIcon} boxSize={6} color={isPopular ? "status.success" : "brand.accent"} />
              <Heading size="lg" color={isPopular ? "status.success" : "text.primary"}>
                <FormattedMessage id={`${plan.name}PlanText`} />
                  </Heading>
            </HStack>
            
            <VStack spacing={1}>
              <Text fontSize="4xl" fontWeight="bold" color="brand.accent">
                ${plan.price}
              </Text>
              <Text fontSize="sm" color="text.secondary">
                /<FormattedMessage id="MonthText" />
              </Text>
            </VStack>

            <Text fontSize="lg" fontWeight="semibold" color="text.secondary" textAlign="center">
              {plan.lookupsTotal === "infinite" ? "∞" : plan.lookupsTotal} <FormattedMessage id="LookupsPerMonthText" />
            </Text>
          </VStack>

          <Divider />

          {/* Main features */}
          <VStack spacing={2} align="stretch">
            {isFreePlan ? (
              // For free plan show only basic features
              <>
                <HStack spacing={3}>
                  <Icon as={FaCheckCircle} color="status.success" boxSize={4} />
                  <Text fontSize="sm" color="text.primary">
                    <FormattedMessage id="GeolocationPricingText" />
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <Icon as={FaCheckCircle} color="status.success" boxSize={4} />
                  <Text fontSize="sm" color="text.primary">
                    <FormattedMessage id="ASNPricingText" />
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <Icon as={FaCheckCircle} color="status.success" boxSize={4} />
                  <Text fontSize="sm" color="text.primary">
                    <FormattedMessage id="BasicSupportPricingText" />
                  </Text>
                </HStack>
              </>
            ) : (
              // For paid plans show all features
              basicBenefits.slice(0, 6).map((benefit, index) => (
                <HStack key={index} spacing={3}>
                  <Icon as={FaCheckCircle} color="status.success" boxSize={4} />
                  <Text fontSize="sm" color="text.primary">
                    <FormattedMessage id={`${benefit}PricingText`} />
                  </Text>
                </HStack>
              ))
            )}
          </VStack>

          <Divider />

          {/* Button or status */}
          <VStack spacing={3}>
            {isSubscriptionAvailable && (
              <BuySubscriptionButtonTable subscription={plan} />
            )}
            
                  {isFreePlan && (
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color="text.primary">
                  <FormattedMessage id="FreeForeverText" defaultMessage="Free Forever" />
                </Text>
                <Text fontSize="sm" color="text.secondary">
                  <FormattedMessage id="NoCreditCardText" defaultMessage="No Credit Card" />
                      </Text>
              </VStack>
                  )}
            
                  {isCurrentSubscription && (
              <VStack spacing={2}>
                  <Badge colorScheme="green" variant="solid" fontSize="md" px={3} py={1}>
                  <FormattedMessage id="CurrentPlanText" defaultMessage="Current Plan" />
                </Badge>
                <Text fontSize="sm" color="text.secondary">
                  <FormattedMessage id="ActiveText" defaultMessage="Active" />
                </Text>
              </VStack>
            )}
          </VStack>
        </VStack>
          </Box>
        </BoxShadowContainer>
      </Box>
    );
  };

  return balance >= 0 || !!balance ? (
    <Box maxW="100%" mx="auto" px={4}>
      <Center mb={8}>
        <VStack spacing={4}>
          <Heading size="xl" textAlign="center" bgGradient="linear(to-r, brand.accent, text.accent)" bgClip="text">
            <FormattedMessage id="ChooseYourPlanText" defaultMessage="Choose Your Plan" />
          </Heading>
          <Text fontSize="lg" color="text.secondary" textAlign="center" maxW="600px">
            <FormattedMessage id="PricingDescriptionText" defaultMessage="Get access to powerful IP analysis tools with our flexible subscription plans" />
          </Text>
        </VStack>
      </Center>
      
      <SimpleGrid 
        columns={{ base: 1, md: 2, lg: 3, xl: 4 }} 
        spacing={6} 
        maxW="1400px" 
        mx="auto"
      >
        {pricingList.map((plan) => (
          <PricingCard 
            key={plan.id} 
            plan={plan} 
            isPopular={plan.id === 2} 
          />
        ))}
      </SimpleGrid>
    </Box>
  ) : (
    <Center>
      <LoaderSpinner />
    </Center>
  );
};

export default PricingTable;
